import { Button } from '@design/components/button/Button';
import { AutocompleteInput } from '@design/components/dropdown/autocomplete-input/AutocompleteInput';
import { Dropdown, DropdownProps } from '@design/components/dropdown/Dropdown';
import { Icon } from '@design/components/icon/Icon';
import { Modal, ModalProps } from '@design/components/modal/Modal';
import { Portal } from '@design/components/portal';
import styles from '@terminal/components/search-modal/SearchModal.module.scss';
import { useDropdownKeyboardEvents } from '@terminal/hooks/use-dropdown-keyboard-events/useDropdownKeyboardEvents';
import clsx from 'clsx';
import React, { Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface SearchModalBaseProps<T> extends ModalProps {
  searchValue: string;
  handleSearch: (value: string) => void;
  selectItem: DropdownProps<T>['selectItem'];
  dropdownItems: DropdownProps<T>['dropdownItems'];
  isLoading: boolean;
  noSearchResultsLabel: ReactNode;
  renderDropdownItem: DropdownProps<T>['renderDropdownItem'];
  value?: string;
  placeholder?: string;
  focusedItemIdx?: number;
  beforeResultsNode?: DropdownProps<T>['beforeResultsNode'];
  afterInListNode?: DropdownProps<T>['afterInListNode'];
  shouldCloseOnSelect?: DropdownProps<T>['shouldCloseOnSelect'];
  loadingNode?: ReactNode;
  shouldResetScrollOnItemsChange?: boolean;
  searchInputVariant?: 'default' | 'secondary';
  showHeader?: boolean;
}

export const SearchModalBase = <T,>({
  dropdownItems,
  searchValue,
  value = searchValue,
  isLoading,
  placeholder,
  beforeResultsNode,
  noSearchResultsLabel,
  shouldCloseOnSelect,
  afterInListNode,
  focusedItemIdx = -1,
  loadingNode,
  shouldResetScrollOnItemsChange = true,
  searchInputVariant = 'default',
  showHeader = true,
  selectItem,
  handleSearch,
  renderDropdownItem,
  ...rest
}: SearchModalBaseProps<T>) => {
  const { t } = useTranslation('common');
  const showNoSearchResults =
    !!value.length && !dropdownItems.length && !isLoading;
  const [focusedText, setFocusedText] = useState<string>();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isSecondaryVariant = searchInputVariant === 'secondary';

  const wrappedBeforeResultsNode =
    isSecondaryVariant && !showHeader ? (
      <div
        className={clsx(styles.beforeResultsWrapper, styles.hasRoundedCorners)}
      >
        {beforeResultsNode}
      </div>
    ) : (
      beforeResultsNode
    );

  useEffect(() => {
    if (focusedText) {
      setFocusedText(undefined);
    }
  }, [searchValue, value, beforeResultsNode]);

  const hasInput = !!focusedText?.length || !!searchValue.length;

  const handleClearMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  const handleClear = () => {
    handleSearch('');

    if (focusedText) {
      setFocusedText(undefined);
    }

    searchInputRef.current?.focus();
  };

  const clearButton = hasInput && (
    <Button
      className={styles.styledButton}
      data-testid="clear-button"
      tabIndex={-1}
      onMouseDown={handleClearMouseDown}
      onClick={handleClear}
      iconName="CloseCircle"
      size="small"
      variant="empty"
    />
  );

  const topBarRightButtons = hasInput && (
    <div className={styles.topBarButtonsContainer}>
      <button
        className={styles.topBarClearButton}
        data-testid="top-bar-clear-button"
        onMouseDown={handleClearMouseDown}
        onClick={handleClear}
      >
        {t('searchClear')}
      </button>
      <div className={styles.topBarDivider} />
      <Icon
        className={styles.topBarCloseButton}
        data-testid="top-bar-close-button"
        iconName="Close"
        onClick={rest.onClose}
        size={12}
      />
    </div>
  );

  const dropdownApiRef = useDropdownKeyboardEvents();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSearch(e.target.value);

    if (focusedText) {
      setFocusedText(undefined);
    }
  };

  const defaultSearchInput = (
    <AutocompleteInput
      ref={searchInputRef}
      className={clsx(
        styles.styledAutocompleteInput,
        styles.autocompleteSecondary,
        { [styles.autocompleteNoHeader]: !showHeader }
      )}
      data-testid="search-input"
      value={focusedText ?? searchValue}
      onChange={handleInputChange}
      placeholder={placeholder}
      leftIcon={<Icon iconName="Search" size={20} />}
      rightIcon={clearButton}
      autoFocus
    />
  );

  const topBarSearchInput = (
    <Portal>
      <div className={styles.topBarSearchContainer}>
        <AutocompleteInput
          ref={searchInputRef}
          className={styles.topBarSearchInput}
          data-testid="search-input"
          value={focusedText ?? searchValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          leftIcon={<Icon iconName="Search" size={20} />}
          rightIcon={topBarRightButtons}
          autoFocus
        />
      </div>
    </Portal>
  );

  const modalClassName = clsx(styles.styledModal, {
    [styles.secondaryVariant]: isSecondaryVariant,
    [styles.hideHeader]: !showHeader,
  });

  if (isLoading && loadingNode) {
    return (
      <Fragment>
        {isSecondaryVariant && topBarSearchInput}
        <Modal className={modalClassName} {...rest}>
          {loadingNode}
        </Modal>
      </Fragment>
    );
  }

  return (
    <Fragment>
      {isSecondaryVariant && topBarSearchInput}
      <Modal className={modalClassName} {...rest}>
        <Dropdown<T>
          className={clsx(styles.styledDropdown, {
            [styles.isSecondaryVariant]: isSecondaryVariant,
          })}
          enableFloating={false}
          shouldCloseOnOutsideClick={false}
          shouldCloseOnSelect={shouldCloseOnSelect}
          shouldResetScrollOnItemsChange={shouldResetScrollOnItemsChange}
          shouldDisableItemMouseEvents
          selectItem={selectItem}
          dropdownItems={dropdownItems}
          autocompleteInputValue={value}
          focusedItemIdx={focusedItemIdx}
          renderDropdownItem={(item, isActive, isFocused, isDisabled) => {
            return renderDropdownItem(
              item,
              isActive,
              isFocused,
              isDisabled,
              setFocusedText
            );
          }}
          beforeResultsNode={wrappedBeforeResultsNode}
          afterResultsNode={
            showNoSearchResults && (
              <p className={styles.helpText} data-testid="no-search-results">
                {noSearchResultsLabel}
              </p>
            )
          }
          afterInListNode={afterInListNode}
          dropdownApiRef={dropdownApiRef}
        >
          {isSecondaryVariant ? <Fragment /> : defaultSearchInput}
        </Dropdown>
      </Modal>
    </Fragment>
  );
};
